import { useMutation } from '@tanstack/react-query';
import {
  type DashboardWidgetV2,
  gqlClient,
  validateDashboardWidgets,
} from '@tyro/api';
import { nanoid } from 'nanoid';

const GENERATE_REPORT_LAYOUT_QUERY = /* GraphQL */ `
  query reporting_tyroneGenerateReportLayout(
    $filter: Tyrone_GenerateReportLayoutInput!
  ) {
    tyrone_generateReportLayout(filter: $filter) {
      kind
      widgets
      dashboardFilters
      message
      options
      reasoning
      droppedCount
      conversationId
      totalCost
      totalTokens
    }
  }
`;

type GenerateReportLayoutResponse = {
  tyrone_generateReportLayout: {
    kind: 'WIDGETS' | 'QUESTION';
    widgets: string;
    dashboardFilters: string;
    message: string | null;
    options: string[];
    reasoning: string | null;
    droppedCount: number;
    conversationId: string;
    totalCost: number;
    totalTokens: number;
  };
};

interface GenerateInput {
  prompt: string;
  conversationId?: string | null;
  /**
   * Optional: when provided, generated widgets get their `y` offset by the
   * tallest existing widget so they stack underneath. Callers who'd rather
   * apply offsets themselves (e.g. the popover, where it's the editor that
   * owns the widget state) can omit this.
   */
  existingWidgets?: DashboardWidgetV2[];
}

/**
 * Compute a y offset large enough to clear the bottom edge of the existing
 * widgets, so a new batch can be appended without overlapping. Exported so
 * downstream consumers (notably the editor's apply-widgets handler) can use
 * the same logic when they receive widgets that didn't pass through the hook.
 */
export function computeBaseY(widgets: DashboardWidgetV2[]): number {
  return widgets.reduce(
    (max, widget) =>
      Math.max(
        max,
        ...Object.values(widget.layout ?? {}).map((entry) =>
          entry ? entry.y + entry.h : 0,
        ),
      ),
    0,
  );
}

/**
 * Apply a y offset to every breakpoint of every widget in the given array.
 * Returns a new array; does not mutate the input.
 */
export function offsetWidgetsY(
  widgets: DashboardWidgetV2[],
  baseY: number,
): DashboardWidgetV2[] {
  if (baseY === 0) return widgets;
  return widgets.map((widget) => {
    const layout = widget.layout ?? {};
    const offsetLayout: typeof layout = {};
    for (const [breakpoint, entry] of Object.entries(layout)) {
      if (!entry) continue;
      offsetLayout[breakpoint as keyof typeof layout] = {
        ...entry,
        y: entry.y + baseY,
      };
    }
    return { ...widget, layout: offsetLayout } as DashboardWidgetV2;
  });
}

export type GenerateOutput =
  | {
      kind: 'WIDGETS';
      widgets: DashboardWidgetV2[];
      dashboardFilters: Record<string, unknown>;
      reasoning: string | null;
      droppedCount: number;
      conversationId: string;
      totalCost: number;
      totalTokens: number;
    }
  | {
      kind: 'QUESTION';
      message: string;
      options: string[];
      conversationId: string;
      totalCost: number;
      totalTokens: number;
    };

export function useGenerateReport() {
  return useMutation({
    mutationFn: async ({
      prompt,
      conversationId,
      existingWidgets,
    }: GenerateInput): Promise<GenerateOutput> => {
      const response = await gqlClient.request<GenerateReportLayoutResponse>(
        GENERATE_REPORT_LAYOUT_QUERY,
        { filter: { prompt, conversationId: conversationId ?? null } },
      );
      const result = response.tyrone_generateReportLayout;

      if (result.kind === 'QUESTION') {
        return {
          kind: 'QUESTION',
          message: result.message ?? '',
          options: result.options ?? [],
          conversationId: result.conversationId,
          totalCost: result.totalCost,
          totalTokens: result.totalTokens,
        };
      }

      let parsed: unknown;
      try {
        parsed = JSON.parse(result.widgets);
      } catch (cause) {
        throw new Error('Tyrone returned invalid widget JSON', { cause });
      }
      if (!Array.isArray(parsed)) {
        throw new Error('Tyrone did not return an array of widgets');
      }

      const validated = validateDashboardWidgets(parsed);

      const reIdentified = validated.map(
        (widget) =>
          ({
            ...widget,
            id: nanoid(),
          }) as DashboardWidgetV2,
      );

      const generated = existingWidgets
        ? offsetWidgetsY(reIdentified, computeBaseY(existingWidgets))
        : reIdentified;

      let dashboardFilters: Record<string, unknown> = {};
      try {
        const parsedFilters = JSON.parse(result.dashboardFilters);
        if (
          parsedFilters &&
          typeof parsedFilters === 'object' &&
          !Array.isArray(parsedFilters)
        ) {
          dashboardFilters = parsedFilters as Record<string, unknown>;
        }
      } catch {
        // Backend should always send a valid JSON string; if not, ignore.
      }

      return {
        kind: 'WIDGETS',
        widgets: generated,
        dashboardFilters,
        reasoning: result.reasoning,
        droppedCount: result.droppedCount,
        conversationId: result.conversationId,
        totalCost: result.totalCost,
        totalTokens: result.totalTokens,
      };
    },
  });
}
